import { useState, useEffect, useCallback } from 'react';
import { useFeatureFlag } from './useFeatureFlag';
import { googleAdsService, AdConfig, AdPerformanceMetrics } from '../lib/googleAds';
import { conditionalLog, conditionalError } from '../lib/utils/logger';

interface UseGoogleAdsReturn {
  isEnabled: boolean;
  isAvailable: boolean;
  displayAd: (config: AdConfig) => Promise<HTMLElement | null>;
  trackClick: () => void;
  metrics: AdPerformanceMetrics;
  error: string | null;
  isLoading: boolean;
}

/**
 * React hook for Google Ads integration with feature flag control
 */
export function useGoogleAds(): UseGoogleAdsReturn {
  const { isEnabled } = useFeatureFlag();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<AdPerformanceMetrics>({
    impressions: 0,
    clicks: 0,
    errors: 0,
    loadTime: 0
  });

  const adsEnabled = isEnabled('GOOGLE_ADS_ENABLED');

  // Initialize Google Ads service when feature is enabled
  useEffect(() => {
    if (adsEnabled) {
      setIsLoading(true);
      setError(null);
      
      googleAdsService.initialize()
        .then(() => {
          setError(null);
        })
        .catch((err) => {
          setError(err.message || 'Failed to initialize Google Ads');
          conditionalError('Google Ads initialization error:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [adsEnabled]);

  // Update metrics periodically
  useEffect(() => {
    if (adsEnabled && googleAdsService.isAvailable()) {
      const interval = setInterval(() => {
        setMetrics(googleAdsService.getMetrics());
      }, 5000); // Update every 5 seconds

      return () => clearInterval(interval);
    }
  }, [adsEnabled]);

  const displayAd = useCallback(async (config: AdConfig): Promise<HTMLElement | null> => {
    if (!adsEnabled) {
      conditionalLog('Google Ads: Feature disabled, not displaying ad');
      return null;
    }

    try {
      setError(null);
      const adElement = await googleAdsService.displayAd(config);
      
      // Update metrics after ad display attempt
      setMetrics(googleAdsService.getMetrics());
      
      return adElement;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to display ad';
      setError(errorMessage);
      conditionalError('Google Ads display error:', err);
      return null;
    }
  }, [adsEnabled]);

  const trackClick = useCallback(() => {
    if (adsEnabled && googleAdsService.isAvailable()) {
      googleAdsService.trackClick();
      setMetrics(googleAdsService.getMetrics());
    }
  }, [adsEnabled]);

  return {
    isEnabled: adsEnabled,
    isAvailable: adsEnabled && googleAdsService.isAvailable(),
    displayAd,
    trackClick,
    metrics,
    error,
    isLoading
  };
}